import React, { useState, useMemo } from 'react'
import { usePeople } from '../../hooks/usePeople'
import { useUserSettings } from '../../hooks/useUserSettings'
import { useToast } from '../../hooks/useToast'
import ToastContainer from '../common/ToastContainer'
import ConfirmDialog from '../common/ConfirmDialog'

export default function PersonAdmin({ user, familyId }) {
  const { people, addPerson, updatePerson, deletePerson, isNameTaken } = usePeople(user, familyId)
  const { defaultTreeRoot, setDefaultTreeRoot } = useUserSettings(user)
  const toast = useToast()
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  
  // Editing state
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(null) // Store ID of person being deleted
  const [confirmDelete, setConfirmDelete] = useState(null) // Person to confirm delete
  
  // Filter people based on search
  const filteredPeople = useMemo(() => {
    if (!searchQuery) return people
    const query = searchQuery.toLowerCase()
    return people.filter(person => 
      person.name.toLowerCase().includes(query) ||
      (person.birthYear && person.birthYear.toLowerCase().includes(query)) ||
      (person.deathYear && person.deathYear.toLowerCase().includes(query)) ||
      (person.notes && person.notes.some(note => note.toLowerCase().includes(query)))
    )
  }, [people, searchQuery])
  
  // Pagination
  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage)
  const paginatedPeople = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filteredPeople.slice(start, start + itemsPerPage)
  }, [filteredPeople, currentPage, itemsPerPage])

  // Create form state
  const [createName, setCreateName] = useState('')
  const [createBirthYear, setCreateBirthYear] = useState('')
  const [createDeathYear, setCreateDeathYear] = useState('')
  const [createPhoto, setCreatePhoto] = useState('')
  const [createNotes, setCreateNotes] = useState('')
  const [isCreating, setIsCreating] = useState(false)


  const handleCreate = async (e) => {
    e.preventDefault()
    setIsCreating(true)

    try {
      if (!createName.trim()) {
        toast.error('Name cannot be blank')
        return
      }

      if (await isNameTaken(createName)) {
        toast.error('Another person with that name already exists. Use a nickname or add spaces.')
        return
      }

      const data = { name: createName.trim() }
      if (createBirthYear.trim()) data.birthYear = createBirthYear.trim()
      if (createDeathYear.trim()) data.deathYear = createDeathYear.trim()
      if (createPhoto.trim()) data.photo = createPhoto.trim()
      if (createNotes.trim()) {
        data.notes = createNotes.split('\n').filter(n => n.trim()).map(n => n.trim())
      }

      await addPerson(data)
      setCreateName('')
      setCreateBirthYear('')
      setCreateDeathYear('')
      setCreatePhoto('')
      setCreateNotes('')
      toast.success(`${createName.trim()} added successfully!`)
    } catch (err) {
      toast.error(`Error: ${err.message}`)
    } finally {
      setIsCreating(false)
    }
  }

  const startEdit = (person) => {
    setEditingId(person.id)
    setEditForm({
      name: person.name || '',
      birthYear: person.birthYear || '',
      deathYear: person.deathYear || '',
      photo: person.photo || '',
      notes: person.notes ? person.notes.join('\n') : ''
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({})
  }

  const handleUpdate = async (personId) => {
    setIsSaving(true)

    try {
      if (!editForm.name?.trim()) {
        toast.error('Name cannot be empty')
        return
      }

      const originalPerson = people.find(p => p.id === personId)
      if (editForm.name !== originalPerson.name && await isNameTaken(editForm.name, personId)) {
        toast.error('Another person with that name already exists.')
        return
      }

      const data = { name: editForm.name.trim() }
      if (editForm.birthYear?.trim()) data.birthYear = editForm.birthYear.trim()
      if (editForm.deathYear?.trim()) data.deathYear = editForm.deathYear.trim()
      if (editForm.photo?.trim()) data.photo = editForm.photo.trim()
      if (editForm.notes?.trim()) {
        data.notes = editForm.notes.split('\n').filter(n => n.trim()).map(n => n.trim())
      }

      await updatePerson(personId, data)
      setEditingId(null)
      setEditForm({})
      toast.success(`${editForm.name.trim()} updated successfully!`)
    } catch (err) {
      toast.error(`Error: ${err.message}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          const person = confirmDelete
          setIsDeleting(person.id)
          try {
            await deletePerson(person.id)
            toast.success(`${person.name} deleted successfully!`)
          } catch (err) {
            toast.error(`Error deleting person: ${err.message}`)
          } finally {
            setIsDeleting(null)
          }
        }}
        title="Delete Person"
        message={confirmDelete ? `Are you sure you want to delete ${confirmDelete.name}? This will remove all their relationships.` : ''}
        confirmLabel="Delete"
        danger
      />
      <div className="space-y-6">
      {/* Create Person Form - Collapsible */}
      <div className="glass-card p-6">
        <details className="group" open>
          <summary className="cursor-pointer list-none flex items-center justify-between">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              Add New Person
            </h2>
            <span className="text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </summary>
          
          <form className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4" onSubmit={handleCreate}>
            <div>
              <label htmlFor="create-name" className="glass-label text-sm">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="create-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Enter name"
                className="glass-input w-full text-sm py-2"
                required
              />
            </div>
            <div>
              <label htmlFor="create-birth-year" className="glass-label text-sm">
                Birth Year
              </label>
              <input
                id="create-birth-year"
                value={createBirthYear}
                onChange={(e) => setCreateBirthYear(e.target.value)}
                placeholder="e.g. 1950"
                className="glass-input w-full text-sm py-2"
              />
            </div>
            <div>
              <label htmlFor="create-death-year" className="glass-label text-sm">
                Death Year
              </label>
              <input
                id="create-death-year"
                value={createDeathYear}
                onChange={(e) => setCreateDeathYear(e.target.value)}
                placeholder="e.g. 2020"
                className="glass-input w-full text-sm py-2"
              />
            </div>
            <div>
              <label htmlFor="create-photo" className="glass-label text-sm">
                Photo URL
              </label>
              <input
                id="create-photo"
                type="url"
                value={createPhoto}
                onChange={(e) => setCreatePhoto(e.target.value)}
                placeholder="https://..."
                className="glass-input w-full text-sm py-2"
              />
            </div>
            <div>
              <label htmlFor="create-notes" className="glass-label text-sm">
                Notes
              </label>
              <input
                id="create-notes"
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                placeholder="Add notes"
                className="glass-input w-full text-sm py-2"
              />
            </div>
            <div className="md:col-span-5 flex gap-3 items-start">
              <button 
                type="submit" 
                className="glass-btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Person
                  </>
                )}
              </button>
            </div>
          </form>
        </details>
      </div>

      {/* People Table */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
            All People ({filteredPeople.length})
          </h2>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              className="glass-input text-sm py-2 w-64"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-300/30 dark:border-gray-600/30">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Birth Year</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Death Year</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Photo</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Notes</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Tree Root</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPeople.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'No people found matching your search.' : 'No people yet. Add one above!'}
                  </td>
                </tr>
              ) : (
                paginatedPeople.map((person) => (
                  <tr key={person.id} className="border-b border-gray-200/30 dark:border-gray-700/30 hover:bg-white/30 dark:hover:bg-gray-700/30 transition-colors">
                    {editingId === person.id ? (
                      <>
                        <td className="py-3 px-4">
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            className="glass-input w-full text-sm py-1 px-2"
                            placeholder="Name"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            value={editForm.birthYear}
                            onChange={(e) => setEditForm({...editForm, birthYear: e.target.value})}
                            className="glass-input w-full text-sm py-1 px-2"
                            placeholder="1950"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            value={editForm.deathYear}
                            onChange={(e) => setEditForm({...editForm, deathYear: e.target.value})}
                            className="glass-input w-full text-sm py-1 px-2"
                            placeholder="2020"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            value={editForm.photo}
                            onChange={(e) => setEditForm({...editForm, photo: e.target.value})}
                            className="glass-input w-full text-sm py-1 px-2"
                            placeholder="URL"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <textarea
                            value={editForm.notes}
                            onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                            className="glass-input w-full text-sm py-1 px-2 resize-none"
                            placeholder="Notes"
                            rows="2"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleUpdate(person.id)}
                              className="glass-btn-primary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Save"
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="glass-btn-secondary glass-btn-sm"
                              title="Cancel"
                              disabled={isSaving}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {person.name}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {person.birthYear || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {person.deathYear || '-'}
                        </td>
                        <td className="py-3 px-4">
                          {person.photo ? (
                            <img 
                              src={person.photo.match(/^https?:\/\//) ? person.photo : `/photos/${person.photo}`}
                              alt={person.name}
                              className="w-10 h-10 rounded-lg object-cover border border-white/20 dark:border-white/10"
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                          {person.notes ? person.notes.join(', ') : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {defaultTreeRoot === person.id ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              ⭐ Your Root
                            </span>
                          ) : (
                            <button
                              onClick={async () => {
                                try {
                                  await setDefaultTreeRoot(person.id)
                                  toast.success(`Set ${person.name} as your default tree root`)
                                } catch (err) {
                                  toast.error('Failed to set tree root')
                                }
                              }}
                              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 text-sm underline"
                              title="Set as your default tree root"
                            >
                              Set Root
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEdit(person)}
                              className="glass-btn-secondary glass-btn-sm"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setConfirmDelete(person)}
                              className="glass-btn-danger glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete"
                              disabled={isDeleting === person.id}
                            >
                              {isDeleting === person.id ? (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-300/30 dark:border-gray-600/30">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPeople.length)} of {filteredPeople.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="glass-btn-secondary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`glass-btn-sm min-w-[2rem] ${
                        currentPage === page
                          ? 'glass-btn-primary'
                          : 'glass-btn-secondary'
                      }`}
                    >
                      {page}
                    </button>
                  )
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="text-gray-500">...</span>
                }
                return null
              })}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="glass-btn-secondary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
